import React from 'react';

import {useI18n} from '@udemy/i18n';
import {
    CourseUnit,
    RecommendationsComponent,
    RecommendationsComponentProps,
} from '@udemy/react-discovery-units';
import {useFeatures} from '@udemy/react-experimentation-provider';

import {CART_RECOMMENDATION_FEATURES} from 'src/lib/experimentation-helper';

type RecommendationsProps = RecommendationsComponentProps & {
    showAddToCartInCard?: boolean;
    showAddToCartInQuickview?: boolean;
    isMobile: boolean;
};

export const Recommendations: React.FC<RecommendationsProps> = (props) => {
    const {gettext} = useI18n();
    const isMobile = props.isMobile;
    const {showAddToCartInCard, showAddToCartInQuickview = true} = props;

    const {featureVariantAssignments} = useFeatures([CART_RECOMMENDATION_FEATURES]);

    const restrictToThreeRecommendationsFeature =
        featureVariantAssignments.get(CART_RECOMMENDATION_FEATURES)?.configuration
            .restrictToThreeRecommendations ?? false;

    const useStackedCardsOnMobile =
        restrictToThreeRecommendationsFeature && isMobile && !!showAddToCartInCard;

    const getUnitComponentAndProps = () => {
        return {
            component: CourseUnit,
            componentProps: {
                allowAddToCartSuccessModal: !showAddToCartInCard,
                fullWidth: isMobile,
                showPager: !isMobile,
                gridCols: isMobile ? 1 : showAddToCartInCard ? 4 : undefined,
                showAddToCartInCourseCard: !!showAddToCartInCard,
                showAddToCartInQuickview,
                renderExperimentalCourseCards: isMobile ? false : !!showAddToCartInCard,
                cartButtonTextGoToCart: showAddToCartInCard ? gettext('In your cart') : undefined,
                cartButtonDisableOnGoToCart: !!showAddToCartInCard,
                disableMobileAddToCartRedirect: true,
                layout: isMobile ? (showAddToCartInCard ? 'singlecol' : 'multirow') : 'singlerow',
                ...(useStackedCardsOnMobile
                    ? {
                          disableInfiniteScroll: true,
                          maxCards: 3,
                          maxCardsPerStack: 3,
                      }
                    : {}),
            },
        };
    };

    return (
        <RecommendationsComponent
            unitPropsByType={{
                CourseUnit: {
                    allowAddToCartSuccessModal: !showAddToCartInCard,
                    fullWidth: isMobile,
                    showPager: !isMobile,
                    gridCols: isMobile ? 1 : showAddToCartInCard ? 4 : undefined,
                    showAddToCartInCourseCard: !!showAddToCartInCard,
                    showAddToCartInQuickview,
                    renderExperimentalCourseCards: isMobile ? false : !!showAddToCartInCard,
                    cartButtonTextGoToCart: showAddToCartInCard
                        ? gettext('In your cart')
                        : undefined,
                    cartButtonDisableOnGoToCart: !!showAddToCartInCard,
                    disableMobileAddToCartRedirect: true,
                    layout: isMobile
                        ? showAddToCartInCard
                            ? 'singlecol'
                            : 'multirow'
                        : 'singlerow',
                },
            }}
            {...{...props, getUnitComponentAndProps: getUnitComponentAndProps}}
        />
    );
};
